import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { X, Minus, Plus, ImageOff } from "lucide-react";
import { formatPrice, resolveUrl, resolveEffectivePrice } from "../webOrderApi";

// Ported from web_order/src/components/ProductModal.tsx — same layout, but:
//   - the "Customisation" weight picker (hardcoded 250g/500g/1kg in the
//     original) now renders the product's real, Active variants. A product
//     with no variants skips that section entirely instead of showing it
//     with three options that don't apply.
//   - the single static image is now a full gallery (thumbnails + a main
//     image switcher) when the product has more than one photo — the
//     product CARD (outside) still only ever shows the first image as the
//     cover; this modal is "inside", where every image is browsable.
//   - the main image uses the same aspect-square + object-cover convention
//     the seller portal itself uses for product photos (PublicProductPage/
//     ProductDetailsPage), instead of a fixed non-square height — a
//     non-square container combined with object-cover was cropping
//     inconsistently depending on each photo's actual aspect ratio.
// Keeping the listing card and this modal in sync means both always read
// the SAME quantity for a given product+variant off the shared cart state,
// rather than each keeping their own independent counter — see the effects
// below and WebOrderPage's addToCart (SETs the line to an absolute value,
// doesn't add on top).
function cartKeyFor(productId, variantId) {
  return `${productId}-${variantId || "base"}`;
}

// The variant the picker opens with pre-selected — the cheapest Active AND
// in-stock one, so it always matches the price ProductCard shows on the
// listing (a customer tapping a ₹525 card shouldn't land on the ₹1,050
// option selected) — while never silently defaulting to an unavailable
// option just because it happened to be cheapest (or first) among ties. An
// out-of-stock variant can only end up selected by explicitly editing an
// existing cart line for it (see resolveInitialVariant/preselectVariantId);
// it's never the picker's own default. Only falls back to considering
// out-of-stock variants at all when literally every one of them is
// unavailable, so the picker still has SOMETHING selected to show.
function getCheapestVariant(product) {
  const variants = (product?.variants || []).filter((v) => v.status !== "Inactive");
  if (variants.length === 0) return null;
  const priceOf = (v) =>
    Number(v.price) > 0 ? resolveEffectivePrice(v.price, v.salePrice) : resolveEffectivePrice(product.price, product.salePrice);
  const inStock = variants.filter((v) => Number(v.stock) > 0);
  const pool = inStock.length > 0 ? inStock : variants;
  return pool.reduce((cheapest, v) => (priceOf(v) < priceOf(cheapest) ? v : cheapest), pool[0]);
}

// Editing an existing cart line (opened from Checkout) should default to
// whatever variant that line is already for, not reset to the cheapest one
// — falls back to cheapest if that variant is missing/inactive (e.g. the
// seller deactivated it after it was added to the cart).
function resolveInitialVariant(product, preselectVariantId) {
  const variants = (product?.variants || []).filter((v) => v.status !== "Inactive");
  if (variants.length === 0) return null;
  const preselected = preselectVariantId ? variants.find((v) => v.id === preselectVariantId) : null;
  return preselected || getCheapestVariant(product);
}

// Same cheapest-in-stock rule as getCheapestVariant above, but scoped to an
// arbitrary variant list — used for picking each independent group's own
// default selection (see variantGroups below), where "cheapest overall"
// would be meaningless since every group needs its OWN default.
function cheapestOf(variantList, product) {
  if (!variantList || variantList.length === 0) return null;
  const priceOf = (v) =>
    Number(v.price) > 0 ? resolveEffectivePrice(v.price, v.salePrice) : resolveEffectivePrice(product.price, product.salePrice);
  const inStock = variantList.filter((v) => Number(v.stock) > 0);
  const pool = inStock.length > 0 ? inStock : variantList;
  return pool.reduce((cheapest, v) => (priceOf(v) < priceOf(cheapest) ? v : cheapest), pool[0]);
}

// One selectable row — shared by the normal single-list "Customisation" box
// and each independent group's own box below, so the two never drift apart
// visually. `isSelected`/`onSelect` are handed in rather than reading
// component state directly, since the two callers track selection very
// differently (one variantId vs. one-per-group).
// useMainPrice (only ever true in combination mode — see ProductModal's own
// useMainPrice below) forces every option to the product's own price and
// hides the per-option price entirely, since every option is guaranteed the
// same price anyway — showing it would just repeat the same number.
function VariantOptionRow({ variant, product, isSelected, onSelect, useMainPrice = false }) {
  const variantOriginal = useMainPrice
    ? Number(product.price)
    : Number(variant.price) > 0 ? Number(variant.price) : Number(product.price);
  const variantEffective = useMainPrice
    ? resolveEffectivePrice(product.price, product.salePrice)
    : Number(variant.price) > 0
      ? resolveEffectivePrice(variant.price, variant.salePrice)
      : resolveEffectivePrice(product.price, product.salePrice);
  const variantOnSale = variantEffective < variantOriginal;
  const outOfStock = Number(variant.stock) <= 0;
  return (
    <label
      onClick={() => !outOfStock && onSelect()}
      className={`py-3 flex items-start justify-between px-2 rounded-xl transition-colors ${
        outOfStock ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50/60"
      }`}
    >
      <div className="min-w-0 flex-1 pr-3">
        <span className="text-sm font-bold text-slate-900">
          {variant.label}
          {outOfStock && <span className="ml-2 text-[10px] font-bold text-yellow-500 uppercase">Out of stock</span>}
        </span>
        {variant.description && (
          <span className="mt-0.5 block break-words text-[11px] font-normal leading-snug text-slate-400">{variant.description}</span>
        )}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {!useMainPrice && (
          <span className="flex items-baseline gap-1.5">
            <span className="text-sm font-extrabold text-slate-900">{formatPrice(variantEffective)}</span>
            {variantOnSale && (
              <span className="text-xs font-semibold text-slate-400 line-through">{formatPrice(variantOriginal)}</span>
            )}
          </span>
        )}
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            isSelected ? "border-brand-900 bg-white" : "border-slate-300 bg-white"
          }`}
        >
          {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-brand-900" />}
        </div>
      </div>
    </label>
  );
}

export default function ProductModal({
  product,
  isOpen,
  onClose,
  onAddToCart,
  cartItemQuantities = {},
  preselectVariantId = null,
  editingCartKey = null,
  preselectVariantIds = null,
  editingGroupProductId = null,
  editingGroupQuantity = null,
  onRemoveCartItem,
  onRemoveProductGroup,
}) {
  const variants = (product?.variants || []).filter((v) => v.status !== "Inactive");
  // independentVariants: each variant carries exactly ONE attribute (see
  // Product.independentVariants) — grouped here by that attribute's name so
  // each group renders as its own selection box instead of one flat list.
  // Order matches first-seen order among variants, which is the order the
  // seller defined attributes in (see ProductForm's sortOrder).
  const isIndependentMode = product?.independentVariants === true;
  // Applies in both combination and independent mode — each independently-
  // selected group already becomes its own separate cart line (see
  // handleAdd below), never summed together, so forcing each one to the
  // main product price is consistent either way, not just in combination
  // mode.
  const useMainPrice = product?.useMainProductPrice === true;
  const variantGroups = (() => {
    if (!isIndependentMode) return [];
    const order = [];
    const byName = new Map();
    variants.forEach((v) => {
      const groupName = Object.keys(v.attributes || {})[0] || "Options";
      if (!byName.has(groupName)) {
        byName.set(groupName, []);
        order.push(groupName);
      }
      byName.get(groupName).push(v);
    });
    return order.map((name) => ({ name, variants: byName.get(name) }));
  })();

  const [selectedVariantId, setSelectedVariantId] = useState(
    resolveInitialVariant(product, preselectVariantId)?.id || null
  );
  // Independent mode only — one selected variantId per group name, e.g.
  // { "Cold Salad": "<variantId>", "Hot Salad": "<variantId>" }.
  const [selectedByGroup, setSelectedByGroup] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [showMore, setShowMore] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageBroken, setImageBroken] = useState(false);
  const [brokenThumbs, setBrokenThumbs] = useState(() => new Set());
  // Points at the Customisation/variant-options card (whichever one renders
  // first — the single "Customisation" card in combination mode, or the
  // first independent-mode group) so the picker can open pre-scrolled
  // straight to it instead of the product image, since picking a variant is
  // the actual thing the customer needs to do here.
  const customisationRef = useRef(null);

  // Reset local state whenever a different product is opened — quantity
  // starts from whatever's ALREADY in the cart for the pre-selected variant
  // (or the base product, if it has none), not always 1. Independent mode
  // starts from the group's existing shared quantity when re-opened to edit
  // it (editingGroupQuantity), else 1 for a fresh add.
  useEffect(() => {
    if (!product) return;
    if (!isIndependentMode) {
      const initialVariant = resolveInitialVariant(product, preselectVariantId);
      setSelectedVariantId(initialVariant?.id || null);
      setQuantity(cartItemQuantities[cartKeyFor(product.id, initialVariant?.id)] || 1);
    } else {
      setQuantity(editingGroupQuantity || 1);
    }
    setShowMore(false);
    setActiveImageIndex(0);
    setImageBroken(false);
    setBrokenThumbs(new Set());
    // Only re-run when a different product opens — deliberately not
    // depending on cartItemQuantities itself, or every cart change anywhere
    // in the app would reset the modal's in-progress selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, preselectVariantId, isIndependentMode, editingGroupQuantity]);

  // Independent mode — default each group to its own cheapest-in-stock
  // option, or whichever option is already selected for that group when
  // re-opened to edit an existing set (preselectVariantIds — the WHOLE
  // group's current lines, not just one). Separate effect from the one
  // above since it needs variantGroups, which is recomputed fresh every
  // render rather than a stable dependency. Keyed on the joined id list
  // (not the array itself) so it only re-runs when the actual preselected
  // ids change, not on every unrelated re-render while the modal is open —
  // otherwise it would keep stomping the customer's in-progress changes.
  const preselectVariantIdsKey = (preselectVariantIds || []).join(",");
  useEffect(() => {
    if (!product || !isIndependentMode) return;
    const initial = {};
    variantGroups.forEach((g) => {
      const preselected =
        (preselectVariantIds || []).find((id) => g.variants.some((v) => v.id === id)) ||
        (preselectVariantId && g.variants.some((v) => v.id === preselectVariantId) ? preselectVariantId : null);
      initial[g.name] = preselected || cheapestOf(g.variants, product)?.id || null;
    });
    setSelectedByGroup(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, preselectVariantIdsKey, preselectVariantId, isIndependentMode]);

  // Switching variants inside the modal re-syncs quantity to THAT variant's
  // own current cart amount, same reasoning as above. Non-independent mode only.
  useEffect(() => {
    if (!product || isIndependentMode) return;
    setQuantity(cartItemQuantities[cartKeyFor(product.id, selectedVariantId)] || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantId]);

  // Opens straight to the Customisation card instead of the product image —
  // picking a variant is the one thing that's actually required here, so
  // that's what should be in front of the customer the instant the picker
  // opens. useLayoutEffect (not useEffect) so this happens before the
  // browser paints the opening frame — otherwise the image would flash
  // briefly before jumping down. No-op for a no-variant product (the ref
  // never attaches to anything, since that card doesn't render at all).
  useLayoutEffect(() => {
    if (!product) return;
    customisationRef.current?.scrollIntoView({ block: "start" });
  }, [product]);

  // Without this, the page underneath can still catch a touch-scroll on
  // some mobile browsers even though this modal renders as a `fixed`
  // overlay on top of it — the page scrolls invisibly behind the overlay,
  // which can drag the "fixed" panel along with it on certain Android
  // WebViews/Chrome versions, making the header (and its close button)
  // appear to scroll away. Restores whatever the page's own overflow was
  // (not assumed to be the default) once the modal closes.
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  if (!isOpen || !product) return null;

  const outOfStock = product.outOfStock === true;
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) || null;
  // Belt-and-suspenders on top of getCheapestVariant never DEFAULTING to an
  // out-of-stock variant: this also covers the one legitimate way one CAN
  // still end up selected — editing an existing cart line whose variant went
  // out of stock since it was added (see preselectVariantId/resolveInitialVariant).
  const selectedVariantOutOfStock = selectedVariant != null && Number(selectedVariant.stock) <= 0;
  const unitPrice = useMainPrice
    ? resolveEffectivePrice(product.price, product.salePrice)
    : selectedVariant && Number(selectedVariant.price) > 0
      ? resolveEffectivePrice(selectedVariant.price, selectedVariant.salePrice)
      : resolveEffectivePrice(product.price, product.salePrice);
  const totalPrice = unitPrice * quantity;
  const images = product.images || [];
  const image = !imageBroken ? resolveUrl(images[activeImageIndex] ?? images[0]) : null;

  // Independent mode — every group needs a selection. Normally the
  // displayed total is the SUM of each selected option's own price (each
  // group is effectively its own separately-priced item being ordered
  // together, e.g. a Cold Salad choice AND a Hot Salad choice), times the
  // shared quantity. But when useMainPrice is on, the selections only
  // decide WHICH variant lines get created (see handleAdd) — the price
  // stays a single main-product charge no matter how many groups are
  // selected, so the total must NOT be summed per group here.
  const allGroupsSelected = isIndependentMode && variantGroups.length > 0 && variantGroups.every((g) => !!selectedByGroup[g.name]);
  const anySelectedGroupOutOfStock = isIndependentMode && variantGroups.some((g) => {
    const v = g.variants.find((x) => x.id === selectedByGroup[g.name]);
    return v != null && Number(v.stock) <= 0;
  });
  const independentUnitTotal = isIndependentMode
    ? useMainPrice
      ? resolveEffectivePrice(product.price, product.salePrice)
      : variantGroups.reduce((sum, g) => {
          const v = g.variants.find((x) => x.id === selectedByGroup[g.name]);
          if (!v) return sum;
          const eff = Number(v.price) > 0 ? resolveEffectivePrice(v.price, v.salePrice) : resolveEffectivePrice(product.price, product.salePrice);
          return sum + eff;
        }, 0)
    : 0;
  const displayTotal = isIndependentMode ? independentUnitTotal * quantity : totalPrice;
  const addDisabled = isIndependentMode
    ? outOfStock || !allGroupsSelected || anySelectedGroupOutOfStock
    : outOfStock || (variants.length > 0 && !selectedVariant) || selectedVariantOutOfStock;
  const isEditingThisGroup = isIndependentMode && editingGroupProductId === product.id;

  const handleAdd = () => {
    if (isIndependentMode) {
      // The cart holds one set of selections per independent-mode product, so
      // saving always replaces its existing lines — also when re-opened from the listing, not only from Checkout.
      onRemoveProductGroup?.(product.id);
      // Every group's current selection becomes its own separate, normal
      // cart line (same product, different variant), all at the same shared
      // quantity. When useMainPrice is on, only the FIRST selected group's
      // line actually carries the price — every other group's line is added
      // at ₹0 so the total stays at one unit of the main price no matter how
      // many groups were selected.
      variantGroups.forEach((g, index) => {
        const chosen = g.variants.find((v) => v.id === selectedByGroup[g.name]);
        if (chosen) onAddToCart(product, chosen, quantity, { zeroPriced: useMainPrice && index > 0 });
      });
      onClose();
      return;
    }
    // Editing an existing line and switching to a different variant —
    // remove the old line so this becomes a change, not a second item.
    // Same variant (just adjusting quantity, or re-confirming) needs no
    // removal: onAddToCart already updates that one line in place.
    const newCartKey = cartKeyFor(product.id, selectedVariant?.id);
    if (editingCartKey && editingCartKey !== newCartKey) {
      onRemoveCartItem?.(editingCartKey);
    }
    onAddToCart(product, selectedVariant, quantity);
    onClose();
  };

  const maxDescLength = 80;
  const hasLongDesc = product.description && product.description.length > maxDescLength;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 animate-in fade-in fill-mode-forwards duration-300">
      <div className="fixed inset-0" onClick={onClose} />

      <div className="product-modal-max-h relative w-full max-w-lg bg-white rounded-t-[28px] sm:rounded-3xl shadow-2xl overflow-hidden z-10 flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-bottom-6 sm:zoom-in-95 fill-mode-forwards duration-300 ease-out">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100 bg-white sticky top-0 z-20">
          <div className="min-w-0 pr-2">
            <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight truncate">
              {product.name}
            </h2>
            {outOfStock && (
              <span className="text-[10px] font-bold text-yellow-500 uppercase">Out of stock</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-brand-900 hover:bg-brand-950 text-white flex items-center justify-center shadow-md transition-all cursor-pointer shrink-0"
          >
            <X className="w-5 h-5 stroke-[3]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {/* No image at all: skip the image/thumbnail section entirely
              rather than showing an empty placeholder box — that reserved-
              space treatment is the product LIST's own rule (MenuProductCard,
              for row/grid alignment), not this popup's. */}
          {image ? (
            <div className="bg-white border border-slate-100 rounded-2xl p-3 sm:p-4 space-y-3 shadow-2xs">
              <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-slate-100 border border-slate-200/60 shadow-2xs">
                <img src={image} alt={product.name} onError={() => setImageBroken(true)} className="w-full h-full object-cover" />
              </div>

              {images.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {images.map((img, i) => {
                    const thumbSrc = !brokenThumbs.has(i) ? resolveUrl(img) : null;
                    const isActive = i === activeImageIndex;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setActiveImageIndex(i);
                          setImageBroken(false);
                        }}
                        className={`aspect-square overflow-hidden rounded-xl border-2 transition-all ${
                          isActive ? "border-brand-900 ring-2 ring-brand-100" : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {thumbSrc ? (
                          <img
                            src={thumbSrc}
                            alt={`${product.name} ${i + 1}`}
                            onError={() => setBrokenThumbs((prev) => new Set(prev).add(i))}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center bg-slate-100">
                            <ImageOff className="w-4 h-4 text-slate-300" strokeWidth={1.5} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {product.description && (
                <div className="text-xs sm:text-sm font-bold text-slate-600 whitespace-pre-line leading-relaxed">
                  {!hasLongDesc || showMore ? (
                    <span>{product.description}</span>
                  ) : (
                    <span>{product.description.slice(0, maxDescLength)}...</span>
                  )}
                  {hasLongDesc && (
                    <button
                      type="button"
                      onClick={() => setShowMore(!showMore)}
                      className="font-extrabold text-brand-900 hover:underline cursor-pointer block mt-1"
                    >
                      {showMore ? "show less" : "...more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            product.description && (
              <div className="bg-white border border-slate-100 rounded-2xl p-3 sm:p-4 shadow-2xs">
                <div className="text-xs sm:text-sm font-bold text-slate-600 whitespace-pre-line leading-relaxed">
                  {!hasLongDesc || showMore ? (
                    <span>{product.description}</span>
                  ) : (
                    <span>{product.description.slice(0, maxDescLength)}...</span>
                  )}
                  {hasLongDesc && (
                    <button
                      type="button"
                      onClick={() => setShowMore(!showMore)}
                      className="font-extrabold text-brand-900 hover:underline cursor-pointer block mt-1"
                    >
                      {showMore ? "show less" : "...more"}
                    </button>
                  )}
                </div>
              </div>
            )
          )}

          {isIndependentMode
            ? variantGroups.map((g, groupIndex) => (
                <div
                  key={g.name}
                  ref={groupIndex === 0 ? customisationRef : undefined}
                  className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 space-y-3 shadow-2xs"
                >
                  <div>
                    <h3 className="text-sm sm:text-base font-extrabold text-[#1E293B] tracking-tight">{g.name}</h3>
                    <p className="text-xs font-semibold text-slate-400 mt-0.5">Required • Select any 1 option</p>
                  </div>
                  <div className="divide-y divide-slate-100 pt-1">
                    {g.variants.map((v) => (
                      <VariantOptionRow
                        key={v.id}
                        variant={v}
                        product={product}
                        isSelected={selectedByGroup[g.name] === v.id}
                        onSelect={() => setSelectedByGroup((prev) => ({ ...prev, [g.name]: v.id }))}
                        useMainPrice={useMainPrice}
                      />
                    ))}
                  </div>
                </div>
              ))
            : variants.length > 0 && (
                <div ref={customisationRef} className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 space-y-3 shadow-2xs">
                  <div>
                    <h3 className="text-sm sm:text-base font-extrabold text-[#1E293B] tracking-tight">Customisation</h3>
                    <p className="text-xs font-semibold text-slate-400 mt-0.5">Required • Select any 1 option</p>
                  </div>
                  <div className="divide-y divide-slate-100 pt-1">
                    {variants.map((v) => (
                      <VariantOptionRow
                        key={v.id}
                        variant={v}
                        product={product}
                        isSelected={selectedVariantId === v.id}
                        onSelect={() => setSelectedVariantId(v.id)}
                        useMainPrice={useMainPrice}
                      />
                    ))}
                  </div>
                </div>
              )}
        </div>

        <div className="p-4 border-t border-slate-100 bg-white flex items-center gap-3 sticky bottom-0 z-20 shadow-2xl">
          <div className="h-12 bg-white border-2 border-brand-200 text-brand-900 rounded-xl px-3 font-black text-sm flex items-center gap-3 shadow-2xs shrink-0">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-4 h-4 flex items-center justify-center hover:bg-brand-50 rounded-md transition-colors cursor-pointer"
            >
              <Minus className="w-3.5 h-3.5 stroke-[3]" />
            </button>
            <span className="min-w-[14px] text-center font-black text-sm">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-4 h-4 flex items-center justify-center hover:bg-brand-50 rounded-md transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5 stroke-[3]" />
            </button>
          </div>

          <button
            onClick={handleAdd}
            disabled={addDisabled}
            className="flex-1 h-12 flex items-center justify-center bg-brand-900 hover:bg-brand-950 disabled:opacity-50 disabled:cursor-not-allowed text-white font-extrabold px-5 rounded-xl text-center text-sm sm:text-base shadow-md transition-all cursor-pointer active:scale-98"
          >
            {outOfStock || (isIndependentMode ? anySelectedGroupOutOfStock : selectedVariantOutOfStock)
              ? "Out of Stock"
              : `${editingCartKey || isEditingThisGroup ? "Update item" : "Add item"} ${formatPrice(displayTotal)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
